import { forwardRef, useId, type TextareaHTMLAttributes } from 'react';

export type TextareaProps = Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> & {
  label: string;
  required?: boolean;
  error?: string;
  helper?: string;
};

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, required, error, helper, className, rows = 5, ...rest },
  ref,
) {
  const generatedId = useId();
  const id = rest.name ? `${rest.name}-${generatedId}` : generatedId;
  const helperId = `${id}-helper`;
  const errorId = `${id}-error`;
  const describedBy =
    [error ? errorId : null, !error && helper ? helperId : null].filter(Boolean).join(' ') ||
    undefined;

  return (
    <div className={`m5-field${error ? ' m5-field--error' : ''}`}>
      <label className="m5-field__label" htmlFor={id}>
        {label}
        {required ? (
          <span className="m5-field__required" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>
      <div className="m5-field__control">
        <textarea
          ref={ref}
          id={id}
          rows={rows}
          className={['m5-field__input', className].filter(Boolean).join(' ')}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          aria-required={required || undefined}
          {...rest}
        />
      </div>
      {error ? (
        <span id={errorId} className="m5-field__error" role="alert">
          {error}
        </span>
      ) : helper ? (
        <span id={helperId} className="m5-field__helper">
          {helper}
        </span>
      ) : null}
    </div>
  );
});
